import * as React from 'react'
import {PRIMARY_BLUE, BORDER, withOpacity} from '../../../../styles/foundation/colors'

/**
 * MIRRORS `freeze-v2/components/CartonSVG.js`.
 *
 * COPIED, NOT IMPORTED. disputes-v2 and freeze-v2 are deliberately independent
 * modules: neither may import from the other, so a shared component travels as
 * a copy and any divergence is stated here rather than hidden behind a prop.
 *
 * Divergence from the original: `toTriple` is inlined as a module-private
 * helper rather than mirrored into a `freeze/weightMath.js`. disputes-v2
 * already owns its volumetric arithmetic in `state/volumetric.js`; a second
 * math file for one three-line normaliser would be a fourth body of weight
 * maths in this module.
 *
 * CARRY-OVER RULE, repeated from the original because it governs where this
 * drawing may be placed: it normalises the LARGEST of the three dimensions
 * (`s = size * 0.62 / max(l,b,h)`), so a 20 cm cube and a 60 cm cube draw
 * identically. What survives the normalisation is proportion — long and flat
 * vs tall and narrow — and nothing else. **Never let a reader take it as
 * to-scale.** That is why the aria-label says "Approximate carton", and why
 * the one caller (the drawer's packing section, which does sit beside a real
 * measured L x B x H triple) prints a visible "Not to scale" caption beneath
 * it.
 *
 * `{dims, size = 132}` is the original's contract and is kept identical, so
 * this file stays diffable against it.
 *
 * @param {number[]|{l:number,b:number,h:number}} dims  length, breadth, height in centimetres
 * @param {number}   size  the longest edge the drawing is allowed to occupy, in px
 */

function toTriple(dims) {
  if (!dims) return null
  return Array.isArray(dims) ? dims : [dims.l, dims.b, dims.h]
}

export default function CartonSVG({dims, size = 132}) {
  // A carton with a missing or non-positive side has no shape to convey, and a
  // zero-width rectangle is worse than nothing — the caller renders its own
  // "dimensions missing" copy instead.
  const triple = toTriple(dims)
  if (!triple || triple.length !== 3) return null
  const [l, b, h] = triple.map(Number)
  if (!(l > 0) || !(b > 0) || !(h > 0)) return null

  const big = Math.max(l, b, h) || 1
  const s = (size * 0.62) / big
  const W = l * s
  const H = h * s
  const D = b * s * 0.5 // the isometric depth foreshortening
  const pad = 14
  const w = W + D + pad * 2
  const ht = H + D + pad * 2

  return (
    <svg
      viewBox={`0 0 ${w} ${ht}`}
      width='100%'
      height={Math.min(ht, size)}
      role='img'
      aria-label={`Approximate carton, ${l} by ${b} by ${h} centimetres`}
    >
      <g transform={`translate(${pad},${pad + D})`}>
        {/* top */}
        <polygon
          points={`0,0 ${D},${-D} ${W + D},${-D} ${W},0`}
          fill={withOpacity(PRIMARY_BLUE, 0.28)} stroke={PRIMARY_BLUE} strokeWidth='1.2'
        />
        {/* right side */}
        <polygon
          points={`${W},0 ${W + D},${-D} ${W + D},${H - D} ${W},${H}`}
          fill={withOpacity(PRIMARY_BLUE, 0.14)} stroke={PRIMARY_BLUE} strokeWidth='1.2'
        />
        {/* front face */}
        <rect
          x='0' y='0' width={W} height={H}
          fill={withOpacity(PRIMARY_BLUE, 0.08)} stroke={PRIMARY_BLUE} strokeWidth='1.2'
        />
        {/* tape seam — the one cue that reads "carton" rather than "cuboid" */}
        <line x1={W * 0.5} y1='0' x2={W * 0.5} y2={H} stroke={BORDER} strokeWidth='2' />
      </g>
    </svg>
  )
}
